import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Card, Table } from 'antd';
import { useModal } from 'modules/common/hooks';
import { usePrbcDonationDonors } from 'modules/dashboard/hooks/usePrbcDonationDonors';
import { useRecruiterDashboardSettings } from 'modules/dashboard/hooks/useRecruiterDashboardSettings';
import { useRegistrationDonors } from 'modules/dashboard/hooks/useRegistrationDonors';
import { IDonorRecord } from 'modules/dashboard/types/donor';
import { UPSERT_DONOR_CONTACT_FLAG_MUTATION } from 'modules/donor/graphql/mutations';
import { GET_DONOR_CONTACT_FLAGS_QUERY } from 'modules/donor/graphql/queries';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PENDING_DAYS_ROW_COLORS } from '../../constants';
import { buildColumns } from '../../donorColumns/buildColumns';
import { buildCsvColumns } from '../../donorColumns/buildCsvColumns';
import {
  DEFAULT_VISIBLE_COLUMNS,
  COLUMN_VISIBILITY_LABELS,
} from '../../donorColumns/columnRegistry';
import {
  LAPSED_DONOR_KEYS,
  PENDING_DONOR_KEYS,
  PRBC_DONOR_KEYS,
  RETIRED_DONOR_KEYS,
  SHARED_DONOR_KEYS,
} from '../../donorColumns/tableColumnKeys';
import { useColumnVisibility } from '../../hooks/useColumnVisibility';
import { useUrlParams } from '../../hooks/useUrlParams';
import { ColumnKey, ITabViewProps, PendingDaysFilter } from '../../types';
import { downloadCsv, makeCsvFilename } from '../../utils/csvUtils';
import {
  getDaysPending,
  getPendingDaysStatus,
} from '../../utils/pendingDaysUtils';
import ColumnVisibilityPanel from '../ColumnVisibilityPanel';
import DonorDetailsHeader from '../DonorDetailsHeader';
import { DonorDonationHistoryModal } from '../DonorDonationHistoryModal';
import PendingDaysFilterBar from '../PendingDaysFilterBar';
import TableToolbar from '../TableToolbar';

const DonorTableView: FC<ITabViewProps> = ({
  meta,
  onBack,
  onLoadActiveDonors,
}) => {
  const { table, filters } = useUrlParams();

  const {
    dataTableId,
    colVisKey,
    isRegistration,
    isDonorTableConfig,
    isLapsed,
    lapsedDays,
    isRetired,
    retiredDays,
    isPending,
    donationUnitType,
  } = table;

  const {
    registrationRange,
    regionIds,
    officeIds,
    locationIds,
    activeDonorStatuses,
  } = filters;

  const currentUser = useCurrentUser();
  const { settings } = useRecruiterDashboardSettings();

  const {
    config: drilldownConfig,
    show: openDrilldown,
    hide: closeDrilldown,
  } = useModal<{ donor: IDonorRecord | null }>({ donor: null });

  const [pendingDaysFilter, setPendingDaysFilter] =
    useState<PendingDaysFilter>('all');

  const filteredDonorsRef = useRef<IDonorRecord[] | null>(null);
  const tableCardRef = useRef<HTMLDivElement>(null);

  const { visibleColumns, handleColumnVisibilityChange } = useColumnVisibility(
    colVisKey,
    DEFAULT_VISIBLE_COLUMNS
  );

  const {
    donors: regDonors,
    total: regTotal,
    loading: regLoading,
  } = useRegistrationDonors({
    enabled: isRegistration,
    registrationRange: isRegistration ? registrationRange : undefined,
    regionIds,
    locationIds,
    officeIds,
    sglThresholdMl: settings?.sglThresholdMl,
  });

  const {
    donors: prbcDonors,
    total: prbcTotal,
    loading: prbcLoading,
    periodRange,
  } = usePrbcDonationDonors({
    registrationRange: isRegistration ? undefined : registrationRange,
    regionIds,
    officeIds,
    locationIds,
    activeDonorStatuses,
    lapsedDays,
    retiredDaysFrom: retiredDays?.from,
    retiredDaysTo: retiredDays?.to,
    donationUnitType,
    sglThresholdMl: settings?.sglThresholdMl,
    enabled: isDonorTableConfig && !isRegistration,
  });

  const donors = isRegistration ? regDonors : prbcDonors;
  const total = isRegistration ? regTotal : prbcTotal;
  const loading = isRegistration ? regLoading : prbcLoading;

  useEffect(() => {
    filteredDonorsRef.current = null;
  }, [donors]);

  const [contactedMap, setContactedMap] = useState<
    Record<string, { contacted: boolean; note?: string }>
  >({});
  const [contactFlagsLoaded, setContactFlagsLoaded] = useState(false);

  const [upsertFlag] = useMutation(UPSERT_DONOR_CONTACT_FLAG_MUTATION);
  const [fetchFlags] = useLazyQuery(GET_DONOR_CONTACT_FLAGS_QUERY, {
    fetchPolicy: 'network-only',
  });

  const handleContactedChange = useCallback(
    (donorId: string, contacted: boolean) => {
      setContactedMap((prev) => ({
        ...prev,
        [donorId]: { ...prev[donorId], contacted },
      }));
      upsertFlag({ variables: { donorId, flagValue: contacted ? 'Y' : 'N' } });
    },
    [upsertFlag]
  );

  useEffect(() => {
    if (!isLapsed && !isRetired) return;
    if (!donors.length) return;
    setContactFlagsLoaded(false);
    const donorIds = donors.map((d) => d.donorId);
    fetchFlags({ variables: { donorIds } }).then((res) => {
      if (res.data?.getDonorContactFlags) {
        try {
          const flags = JSON.parse(res.data.getDonorContactFlags) as Record<
            string,
            { contacted: boolean }
          >;
          setContactedMap((prev) => {
            const next = { ...prev };
            for (const [id, val] of Object.entries(flags)) {
              next[id] = { ...next[id], contacted: val.contacted };
            }
            return next;
          });
        } catch {
          // ignore parse errors
        }
      }
      setContactFlagsLoaded(true);
    });
  }, [donors, isLapsed, isRetired, fetchFlags]);

  const pendingDaysCounts = useMemo(() => {
    if (!isPending || !settings?.pendingDaysBreakpoints)
      return { onTrack: 0, approaching: 0, overdue: 0 };
    const bp = settings.pendingDaysBreakpoints;
    let onTrack = 0;
    let approaching = 0;
    let overdue = 0;
    for (const d of donors) {
      const status = getPendingDaysStatus(getDaysPending(d.registeredAt), bp);
      if (status === 'onTrack') onTrack++;
      else if (status === 'approaching') approaching++;
      else overdue++;
    }
    return { onTrack, approaching, overdue };
  }, [donors, isPending, settings?.pendingDaysBreakpoints]);

  const displayDonors = useMemo(() => {
    if (
      !isPending ||
      pendingDaysFilter === 'all' ||
      !settings?.pendingDaysBreakpoints
    )
      return donors;
    const bp = settings.pendingDaysBreakpoints;
    return donors.filter(
      (d) =>
        getPendingDaysStatus(getDaysPending(d.registeredAt), bp) ===
        pendingDaysFilter
    );
  }, [donors, pendingDaysFilter, isPending, settings?.pendingDaysBreakpoints]);

  const activeKeys = useMemo(
    () =>
      isRetired
        ? RETIRED_DONOR_KEYS
        : isLapsed
        ? contactFlagsLoaded
          ? LAPSED_DONOR_KEYS
          : SHARED_DONOR_KEYS
        : isPending
        ? PENDING_DONOR_KEYS
        : PRBC_DONOR_KEYS,
    [isRetired, isLapsed, isPending, contactFlagsLoaded]
  );

  const activeColumnVisibilityLabels = useMemo(
    () =>
      COLUMN_VISIBILITY_LABELS.filter(({ key }) => activeKeys.includes(key)),
    [activeKeys]
  );

  const columns = useMemo(() => {
    return buildColumns(activeKeys, {
      donors,
      periodStart: periodRange?.start ? new Date(periodRange.start) : undefined,
      periodEnd: periodRange?.end ? new Date(periodRange.end) : undefined,
      pendingDaysBreakpoints: settings?.pendingDaysBreakpoints,
      defaultSortDescend: isRegistration,
      contactedMap,
      onContactedChange: handleContactedChange,
      isAdmin: currentUser.data?.role?.value === 'Admin',
    }).filter((col) => {
      const k = col.key as ColumnKey;
      return !(k in visibleColumns) || visibleColumns[k];
    });
  }, [
    activeKeys,
    donors,
    periodRange,
    isRegistration,
    visibleColumns,
    settings?.pendingDaysBreakpoints,
    contactedMap,
    handleContactedChange,
    currentUser.data?.role?.value,
  ]);

  const exportDonorsCsv = useCallback(() => {
    const keys = isRetired
      ? RETIRED_DONOR_KEYS
      : isPending
      ? PENDING_DONOR_KEYS
      : PRBC_DONOR_KEYS;
    const csvCols = buildCsvColumns(
      keys,
      {
        donors,
        pendingDaysBreakpoints: settings?.pendingDaysBreakpoints,
        pendingDaysLabels: settings?.pendingDaysLabels,
      },
      { isRetired, isPending, visibleColumns }
    );
    downloadCsv(
      csvCols.map((c) => c.label),
      (filteredDonorsRef.current ?? donors).map((d) =>
        csvCols.map((c) => c.value(d))
      ),
      makeCsvFilename(dataTableId || 'donors')
    );
  }, [
    donors,
    dataTableId,
    visibleColumns,
    isRetired,
    isPending,
    settings?.pendingDaysBreakpoints,
    settings?.pendingDaysLabels,
  ]);

  return (
    <div style={{ padding: 0 }}>
      <DonorDetailsHeader
        title={meta.title}
        description={meta.description}
        iconColor={meta.iconColor}
        donorCount={total}
        countLabel="donors"
        loading={loading}
        onBack={onBack}
        onLoadActiveDonors={onLoadActiveDonors}
      />

      <Card bodyStyle={{ padding: '16px 20px' }} ref={tableCardRef}>
        <TableToolbar
          iconColor={meta.iconColor}
          title="Donor List"
          exportDisabled={!donors.length}
          onExport={exportDonorsCsv}
        />

        {(isLapsed || isRetired) && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: 4,
            }}
          >
            <Button
              size="small"
              onClick={() => {
                const container = tableCardRef.current;
                const scrollable =
                  container?.querySelector('.ant-table-body') ||
                  container?.querySelector('.ant-table-content');
                if (scrollable) {
                  scrollable.scrollTo({
                    left: scrollable.scrollWidth,
                    behavior: 'smooth',
                  });
                }
              }}
              type="primary"
              style={{ backgroundColor: '#1565c0', borderColor: '#1565c0' }}
            >
              Scroll to Contacted →
            </Button>
          </div>
        )}

        <ColumnVisibilityPanel
          columns={activeColumnVisibilityLabels}
          visibleColumns={visibleColumns}
          onChange={handleColumnVisibilityChange}
        />

        {isPending && settings?.pendingDaysBreakpoints && (
          <PendingDaysFilterBar
            totalCount={donors.length}
            filter={pendingDaysFilter}
            counts={pendingDaysCounts}
            labels={settings.pendingDaysLabels}
            onChange={setPendingDaysFilter}
          />
        )}

        <Table
          rowKey="donorId"
          columns={columns}
          dataSource={displayDonors}
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={{
            defaultPageSize: 100,
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} donors`,
          }}
          onChange={(_p, _f, _s, extra) => {
            filteredDonorsRef.current =
              extra.currentDataSource as IDonorRecord[];
          }}
          onRow={(record) => {
            const rowStyle: React.CSSProperties = { cursor: 'pointer' };
            if (isPending && settings?.pendingDaysBreakpoints) {
              const days = getDaysPending(record.registeredAt);
              const status = getPendingDaysStatus(
                days,
                settings.pendingDaysBreakpoints
              );
              rowStyle.backgroundColor = PENDING_DAYS_ROW_COLORS[status];
            }
            return {
              onClick: (e) => {
                if ((e.target as HTMLElement).closest('a, button, input'))
                  return;
                openDrilldown({ donor: record });
              },
              style: rowStyle,
            };
          }}
          size="small"
          style={{ marginTop: 8 }}
        />
      </Card>

      <DonorDonationHistoryModal
        donor={drilldownConfig.donor}
        visible={drilldownConfig.visible ?? false}
        onClose={closeDrilldown}
        periodStart={
          periodRange?.start ? new Date(periodRange.start) : undefined
        }
        periodEnd={periodRange?.end ? new Date(periodRange.end) : undefined}
      />
    </div>
  );
};

export default DonorTableView;
